import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, CheckCircle, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrainLogo from "@/components/BrainLogo";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [valid, setValid] = useState(null); // null=loading, true, false, "error"
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const checkToken = () => {
    if (!token) { setValid(false); return; }
    setValid(null);
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(r => setValid(r.data.valid ? true : false))
      .catch(() => setValid("error")); // distinguish network error from invalid token
  };

  useEffect(() => { checkToken(); }, [token]); // eslint-disable-line

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 mesh-bg">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px]"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <BrainLogo size={32} />
          <span className="font-semibold tracking-tight text-[18px]">Studivault</span>
        </Link>

        <AnimatePresence mode="wait">
          {valid === null && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-[#B0AEBC]">
              <Loader2 size={22} className="animate-spin mx-auto mb-3 text-[#B8A4FF]" />
              Validating link…
            </motion.div>
          )}

          {valid === false && (
            <motion.div key="invalid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-900/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <XCircle size={26} className="text-red-400" />
              </div>
              <h1 className="display-3 mb-3">Link expired</h1>
              <p className="text-[15px] text-[#B0AEBC] mb-8">
                This reset link has already been used or expired. Request a fresh one.
              </p>
              <Link to="/forgot-password" className="btn btn-primary w-full block text-center py-3.5">
                Request new link
              </Link>
            </motion.div>
          )}

          {valid === "error" && (
            <motion.div key="neterror" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-900/20 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
                <XCircle size={26} className="text-amber-400" />
              </div>
              <h1 className="display-3 mb-3">Connection error</h1>
              <p className="text-[15px] text-[#B0AEBC] mb-8">
                Couldn't reach the server. Please check your connection and try again.
              </p>
              <button onClick={checkToken} className="btn btn-primary w-full py-3.5">
                Retry
              </button>
            </motion.div>
          )}

          {valid === true && !done && (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <div className="w-14 h-14 rounded-2xl bg-[#1a1a2e] border border-[#2a2a3e] flex items-center justify-center mx-auto mb-4">
                  <KeyRound size={26} className="text-[#B8A4FF]" />
                </div>
                <h1 className="display-3">Set new password</h1>
                <p className="text-[15px] text-[#B0AEBC] mt-2">Choose a strong password for your account.</p>
              </div>

              <form onSubmit={onSubmit} data-testid="reset-password-form" className="space-y-3">
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="new-password-input"
                    className="input"
                    placeholder="At least 8 characters"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Confirm Password</label>
                  <input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="confirm-password-input"
                    className="input"
                    placeholder="Repeat your new password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="btn btn-primary w-full mt-6 py-3.5 text-[15px]"
                >
                  {loading ? "Updating…" : <>Update password <ArrowRight size={15} className="inline ml-1 -mt-0.5" /></>}
                </button>
              </form>
            </motion.div>
          )}

          {done && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-900/20 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={28} className="text-green-400" />
              </div>
              <h1 className="display-3 mb-3">Password updated!</h1>
              <p className="text-[15px] text-[#B0AEBC] mb-8">You can now sign in with your new password. Redirecting…</p>
              <Link to="/login" className="btn btn-primary w-full block text-center py-3.5" data-testid="reset-done-login">
                Sign in now
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
